/**
 * @fileoverview Users API Contract
 * @module types/contracts
 */

import { z } from 'zod';
import {
	createContract,
	extractRoutes,
	ApiSuccessResponseSchema,
	ApiPaginatedResponseSchema,
} from './contract.utils';

// ============================================================================
// ENUMS
// ============================================================================

export enum UserRole {
	USER = 'user',
	ADMIN = 'admin',
}

// ============================================================================
// CORE SCHEMAS
// ============================================================================

/**
 * User Metadata Schema
 */
export const UserMetadataSchema = z
	.object({
		preferences: z.record(z.string(), z.unknown()).optional(),
		utm: z
			.object({
				source: z.string().optional(),
				medium: z.string().optional(),
				campaign: z.string().optional(),
			})
			.optional(),
		consent: z
			.object({
				marketing: z.boolean().optional(),
				analytics: z.boolean().optional(),
				dataProcessing: z.boolean().optional(),
			})
			.optional(),
	})
	.passthrough();
export type UserMetadata = z.infer<typeof UserMetadataSchema>;

/**
 * Create User Schema
 */
export const CreateUserSchema = z.object({
	name: z.string().min(2).describe('User name'),
	email: z.email().describe('User email'),
	role: z.nativeEnum(UserRole).optional().default(UserRole.USER).describe('User role'),
	metadata: UserMetadataSchema.optional().describe('User metadata'),
});
export type CreateUserPayload = z.infer<typeof CreateUserSchema>;

/**
 * User Entity Schema
 *
 * IMPORTANT: Date fields use z.iso.datetime() for ISO 8601 strings in the API contract.
 * The ZodSerializerInterceptor will automatically convert Date objects from DB entities to ISO strings.
 */
export const UserSchema = z.object({
	id: z.uuid().describe('Unique user ID'),
	name: z.string().describe('User name'),
	email: z.email().describe('User email'),
	role: z.nativeEnum(UserRole).describe('User role'),
	metadata: UserMetadataSchema.nullable().optional().describe('User metadata'),
	createdAt: z.iso.datetime().describe('Creation timestamp (ISO 8601)'),
	updatedAt: z.iso.datetime().describe('Last update timestamp (ISO 8601)'),
});
export type User = z.infer<typeof UserSchema>;

/**
 * Update User Schema
 */
export const UpdateUserSchema = z.object({
	name: z.string().min(2).optional(),
	email: z.email().optional(),
	role: z.nativeEnum(UserRole).optional(),
	metadata: UserMetadataSchema.optional(),
});
export type UpdateUserPayload = z.infer<typeof UpdateUserSchema>;

/**
 * User ID Parameter Schema
 */
export const UserIdParamSchema = z.object({
	id: z.uuid('Invalid user ID format'),
});

/**
 * List Users Query Schema
 *
 * IMPORTANT: Use z.coerce.number() for query parameters to generate proper OpenAPI spec
 * - z.string().transform(Number) generates type: "string" in OpenAPI (WRONG)
 * - z.coerce.number() generates type: "integer" in OpenAPI (CORRECT)
 */
export const ListUsersQuerySchema = z.object({
	page: z.coerce.number().int().positive().default(1),
	limit: z.coerce.number().int().positive().max(100).default(10),
	role: z.nativeEnum(UserRole).optional(),
});
export type ListUsersQuery = z.infer<typeof ListUsersQuerySchema>;

// ============================================================================
// API RESPONSE SCHEMAS
// ============================================================================

/**
 * User API Response
 * { success: true, data: User, timestamp: string }
 */
export const UserApiResponseSchema = ApiSuccessResponseSchema(UserSchema);
export type UserApiResponse = z.infer<typeof UserApiResponseSchema>;

/**
 * Paginated Users API Response
 * { success: true, data: User[], meta: {...}, timestamp: string }
 */
export const PaginatedUsersApiResponseSchema = ApiPaginatedResponseSchema(UserSchema);
export type PaginatedUsersApiResponse = z.infer<typeof PaginatedUsersApiResponseSchema>;

/**
 * Delete User API Response
 * { success: true, data: { message: string }, timestamp: string }
 */
const DeleteUserDataSchema = z.object({ message: z.string() });
export const DeleteUserApiResponseSchema = ApiSuccessResponseSchema(DeleteUserDataSchema);
export type DeleteUserApiResponse = z.infer<typeof DeleteUserApiResponseSchema>;

// ============================================================================
// API CONTRACT
// ============================================================================

export const USERS_API_CONTRACT = createContract('/users', {
	create: {
		method: 'POST' as const,
		path: '',
		body: CreateUserSchema,
		response: UserApiResponseSchema,
	},
	list: {
		method: 'GET' as const,
		path: '',
		query: ListUsersQuerySchema,
		response: PaginatedUsersApiResponseSchema,
	},
	getOne: {
		method: 'GET' as const,
		path: '/:id',
		params: UserIdParamSchema,
		response: UserApiResponseSchema,
	},
	update: {
		method: 'PATCH' as const,
		path: '/:id',
		params: UserIdParamSchema,
		body: UpdateUserSchema,
		response: UserApiResponseSchema,
	},
	delete: {
		method: 'DELETE' as const,
		path: '/:id',
		params: UserIdParamSchema,
		response: DeleteUserApiResponseSchema,
	},
});

export const USERS_ROUTES = extractRoutes(USERS_API_CONTRACT);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type UsersApiContract = typeof USERS_API_CONTRACT;
